/**
 * Pure model-catalog logic shared by the Models pages (ported from the old
 * UI's modelEndpointConfig.ts / modelCapabilities.ts / AdminModels.tsx).
 * Labels live in the "adminModels" i18n namespace, keyed by these values.
 */

import type { AiModelResponse, ModelTierDefaultResponse, ReasoningConfig, ReasoningEffort } from "@/api";

/** Endpoint presets plus the "custom" escape hatch (explicit URL + API key). */
export const HOSTINGS = ["azure_openai_eastus2", "azure_openai_westus", "foundry_eastus2", "custom"] as const;
export type Hosting = (typeof HOSTINGS)[number];

export const PROTOCOLS = ["openai_responses", "openai_completions", "anthropic_messages"] as const;
export type Protocol = (typeof PROTOCOLS)[number];

export const MODEL_TYPES = ["chat", "embedding", "image", "audio", "research"] as const;
export type ModelType = (typeof MODEL_TYPES)[number];

export const COST_TIERS = ["$", "$$", "$$$"] as const;

export const CAPABILITY_KEYS = [
  "isReasoning",
  "supportsVision",
  "supportsFunctionCalling",
  "supportsStreaming",
  "supportsCodeInterpreter",
] as const;
export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

/** Canonical order for every reasoning-effort list (MHS-669). */
export const REASONING_EFFORTS: ReasoningEffort[] = ["none", "minimal", "low", "medium", "high", "xhigh", "max"];

/** Display order of the tier defaults. Unknown tiers from the backend follow, by name. */
export const TIER_ORDER = [
  "fast",
  "thorough",
  "router",
  "embedding",
  "image",
  "transcription",
  "retrieval",
  "code_interpreter",
  "title",
  "agent_routing",
  "kg_extraction",
  "kg_answer",
  "vision",
];

const isKnown = <T extends string>(list: readonly T[], value: string | null | undefined): value is T =>
  list.includes(value as T);

export const hostingOf = (model: Pick<AiModelResponse, "hosting">): Hosting =>
  isKnown(HOSTINGS, model.hosting) ? model.hosting : "azure_openai_eastus2";

export const protocolOf = (model: Pick<AiModelResponse, "apiProtocol">): Protocol =>
  isKnown(PROTOCOLS, model.apiProtocol) ? model.apiProtocol : "openai_responses";

/** The URL path suffix an endpoint usually ends with for a protocol. */
export function expectedEndpointSuffix(protocol: string): string {
  return protocol === "anthropic_messages" ? "/anthropic" : "/openai/v1";
}

export function exampleEndpointUrl(protocol: string): string {
  return `https://<resource>.services.ai.azure.com${expectedEndpointSuffix(protocol)}`;
}

/** Warn-only: true when a custom URL doesn't end with the protocol's usual suffix. */
export function endpointSuffixMismatch(url: string, protocol: string): boolean {
  let trimmed = url.trim();
  while (trimmed.endsWith("/")) trimmed = trimmed.slice(0, -1);
  return trimmed !== "" && !trimmed.endsWith(expectedEndpointSuffix(protocol));
}

/** Legacy `provider` derived from protocol + hosting (the backend still stores it). */
export function deriveLegacyProvider(protocol: string, hosting: string): string {
  if (protocol === "anthropic_messages") return "anthropic_foundry";
  if (hosting === "foundry_eastus2") return "azure_foundry";
  return "azure_openai";
}

/** Hosting + protocol for a suggestion that only carries the legacy provider. */
export function endpointFromProvider(provider: string | undefined): { hosting: Hosting; apiProtocol: Protocol } {
  if (provider === "anthropic_foundry") return { hosting: "foundry_eastus2", apiProtocol: "anthropic_messages" };
  if (provider === "azure_foundry" || provider === "azure_ai_foundry") {
    return { hosting: "foundry_eastus2", apiProtocol: "openai_completions" };
  }
  return { hosting: "azure_openai_eastus2", apiProtocol: "openai_responses" };
}

/** Toggle one effort in a reasoning config, keeping canonical order; null when none remain. */
export function toggleEffort(
  config: ReasoningConfig | null,
  effort: ReasoningEffort,
  checked: boolean,
): ReasoningConfig | null {
  const supported = config?.supportedEfforts ?? [];
  const next = REASONING_EFFORTS.filter((e) => (e === effort ? checked : supported.includes(e)));
  if (next.length === 0) return null;
  const current = config?.defaultEffort ?? null;
  return { supportedEfforts: next, defaultEffort: current && next.includes(current) ? current : null };
}

export type SortOption = "name" | "cost" | "status" | "newest";
export const SORT_OPTIONS: SortOption[] = ["name", "newest", "cost", "status"];

const COST_RANK: Record<string, number> = { $: 1, $$: 2, $$$: 3 };
const byName = (a: AiModelResponse, b: AiModelResponse) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

export function sortModels(models: AiModelResponse[], sortBy: SortOption): AiModelResponse[] {
  const sorted = [...models];
  if (sortBy === "cost") {
    const rank = (m: AiModelResponse) => COST_RANK[m.costTier ?? ""] ?? Number.MAX_SAFE_INTEGER;
    return sorted.sort((a, b) => rank(a) - rank(b) || byName(a, b));
  }
  if (sortBy === "status") return sorted.sort((a, b) => Number(b.isEnabled) - Number(a.isEnabled) || byName(a, b));
  if (sortBy === "newest") return sorted.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || byName(a, b));
  return sorted.sort(byName);
}

const CHAT_TIERS = new Set(["fast", "thorough", "router", "title", "agent_routing", "kg_extraction", "kg_answer"]);
const TYPE_FOR_TIER: Record<string, string> = { embedding: "embedding", image: "image", transcription: "audio" };

/**
 * Models that can fill a tier (by type; retrieval needs an Azure OpenAI Responses chat model, vision a vision chat
 * model, code interpreter a Responses chat model with code interpreter on).
 */
export function modelsForTier(tier: string, models: AiModelResponse[]): AiModelResponse[] {
  const type = (m: AiModelResponse) => m.modelType ?? "chat";
  if (CHAT_TIERS.has(tier)) return models.filter((m) => type(m) === "chat");
  if (tier === "vision") return models.filter((m) => type(m) === "chat" && m.supportsVision);
  if (tier === "retrieval") {
    return models.filter(
      (m) => type(m) === "chat" && protocolOf(m) === "openai_responses" && hostingOf(m).startsWith("azure_openai"),
    );
  }
  if (tier === "code_interpreter") {
    return models.filter(
      (m) => type(m) === "chat" && protocolOf(m) === "openai_responses" && m.supportsCodeInterpreter === true,
    );
  }
  const wanted = TYPE_FOR_TIER[tier];
  return wanted ? models.filter((m) => type(m) === wanted) : models;
}

export function orderTiers(defaults: ModelTierDefaultResponse[]): ModelTierDefaultResponse[] {
  const rank = (tier: string) => {
    const i = TIER_ORDER.indexOf(tier);
    return i === -1 ? TIER_ORDER.length : i;
  };
  return [...defaults].sort((a, b) => rank(a.tier) - rank(b.tier) || a.tier.localeCompare(b.tier));
}

/** Tiers an enabled model could become the default for (and isn't already). */
export function assignableTiers(
  model: AiModelResponse,
  models: AiModelResponse[],
  defaults: ModelTierDefaultResponse[],
): ModelTierDefaultResponse[] {
  if (!model.isEnabled) return [];
  return orderTiers(defaults).filter(
    (td) => td.modelId !== model.id && modelsForTier(td.tier, models).some((m) => m.id === model.id),
  );
}

/** Enabled, non-deprecated models of the same type that usage can move to. */
export const replacementCandidates = (model: AiModelResponse, models: AiModelResponse[]) =>
  models.filter(
    (m) =>
      m.id !== model.id && (m.modelType ?? "chat") === (model.modelType ?? "chat") && m.isEnabled && !m.isDeprecated,
  );

export const usageCount = (model: AiModelResponse) => (model.agentCount ?? 0) + (model.chatCount ?? 0);

/** A date-only string ("2026-12-31") as local midnight, so it formats as that day in every time zone. */
export const localDate = (date: string) => new Date(`${date.slice(0, 10)}T00:00:00`);
